import { AsyncPipe } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NgbModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { SepaFacade } from '@zero/self-service/domain';
import { IbanInputDirective } from '@zero/shared/ui-directives';
import { FormateIBANPipe } from '@zero/shared/ui-pipes';
import { TransactionAuthorizationComponent, UserMessagesComponent } from '@zero/shared/ui-web-components';
import {
  ButtonComponent,
  CopyToClipboardComponent,
  IconComponent,
  ModalComponent,
  SwitchComponent,
} from '@zero/shared/ui-web-design-system';
import { AuthPayloadTicket, AuthorizationData, Ticket, TicketData, TransactionAuthorizationResult } from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { BaseComponent } from '../base/base.component';

@Component({
  selector: 'self-service-konto-depot',
  templateUrl: './konto-depot.component.html',
  styleUrls: ['./konto-depot.component.scss'],
  imports: [
    FormsModule,
    IbanInputDirective,
    TransactionAuthorizationComponent,
    UserMessagesComponent,
    CopyToClipboardComponent,
    TranslocoPipe,
    FormateIBANPipe,
    AsyncPipe,
    ButtonComponent,
    SwitchComponent,
    ModalComponent,
    IconComponent,
    NgbTooltipModule,
  ],
})
export class KontoDepotComponent extends BaseComponent implements OnInit {
  @ViewChild('tanInput') public tanInput: ElementRef;
  public edit = false;
  public dataToAuthorize: AuthorizationData<AuthPayloadTicket>;
  public isDirectDebitMandatePossible = false;
  public readonly directDebitEnabled = environment.directDebitEnabled;
  public directDebitMandatePossibleHint = false;
  public withoutDirectDebitMandateHint = false;

  #ticket: Ticket = null;

  readonly #translateService = inject(TranslocoService);
  public readonly sepaFacade = inject(SepaFacade);
  public readonly modal = inject(NgbModal);

  ngOnInit(): void {
    this.customerStateFacade.loadCustomerAccountData();
    this.sepaFacade.loadHasSepa();
  }

  public toggleSepa(hasSepa: boolean): void {
    this.sepaFacade.toggleSepa(hasSepa);
  }

  public setBank(bankName: string): void {
    this.customerBaseData.refAccChangedBank = bankName;
  }

  public setDirectDebitMandatePossibility(isPossible: boolean): void {
    this.isDirectDebitMandatePossible = isPossible;
  }

  public startChangeRefAccount(): void {
    if (!this.customerBaseData.refAccChangedIban) {
      return;
    }
    this.#ticket = new Ticket();
    this.#ticket.customerId = this.currentCustomer.custId;
    this.#ticket.type = 'REFACCOUNT';

    this.#ticket.ticketData = new TicketData();
    this.#ticket.ticketData.additionalProperties = {};
    this.#ticket.ticketData.additionalProperties[TicketData.ATTR_NEW_REFACCOUNT_IBAN] = this.customerBaseData.refAccChangedIban;
    this.#ticket.ticketData.additionalProperties[TicketData.ATTR_NEW_REFACCOUNT_DIRECT_DEBIT_MANDATE] = Boolean(
      this.customerBaseData.refAccChangedNewDirectDebitMandate,
    );

    this.dataToAuthorize = new AuthorizationData<AuthPayloadTicket>(new AuthPayloadTicket(this.#ticket));
    this.edit = true;
  }

  public authorizedTransactionResult(result: TransactionAuthorizationResult): void {
    if (result.success) {
      this.messageService.clearAllMessages();
      this.messageService.addInfoMessage(this.#translateService.translate('user-self-service.konto-depot.edit.success-msg'), true);
      this.edit = false;
      this.dataToAuthorize = undefined;
      this.customerStateFacade.loadCustomerBaseData(true);
    } else if (result.reason === 'CanceledByUser') {
      this.edit = false;
      this.dataToAuthorize = undefined;
    }
  }

  public changeDirectDebitMandate(activated?: boolean): void {
    this.customerBaseData.refAccChangedNewDirectDebitMandate = activated;
    this.withoutDirectDebitMandateHint = Boolean(this.directDebitEnabled && !this.customerBaseData.refAccChangedNewDirectDebitMandate);
  }

  public displayDirectDebitMandateNotPossibleHint(displayHint: boolean): void {
    this.directDebitMandatePossibleHint = displayHint;
  }
}
